import { Divider, Grid, Theme, Typography } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectCurrentModDetails, selectExpandedModsDialog } from 'store/redux-store/placement-mods/selectors';
import { makeStyles } from 'tss-react/mui';
import { ActualHousingType } from './ActualHousingType';
import { AmountToTraveler } from './AmountToTraveler';
import { BillableToClient } from './BillableToClient';
import { HousingProvider } from './HousingProvider';
import { MonthlyLodgingAmount } from './MonthlyLodgingAmount';
import { OtherDepositAmount } from './OtherDepositAmount';
import { OtherDepositDescription } from './OtherDepositDescription';
import { ParkingGarageCharges } from './ParkingGarageCharges';
import { PetAmountRefundableField } from './PetAmountRefundableField';
import { PetDepositAmount } from './PetDepositAmount';
import { PetRentCharges } from './PetRentCharges';
import { Private2BedroomDepositAmount } from './Private2BedroomDepositAmount';
import { Private3BedroomDepositAmount } from './Private3BedroomDepositAmount';
import { ReasonForChange } from './ReasonForChange';
import { RefundableField } from './RefundableField';
import { StandardAmountCharged } from './StandardAmountCharged';
import { ThreeBedroomRefundableField } from './ThreeBedroomRefundableField';
import { TravelerRequestedHousingType } from './TravelerRequestedHousingType';
import { TwoBedroomRefundableField } from './TwoBedroomRefundableField';
import { UtilityCharges } from './UtilityCharges';

const useStyles = makeStyles<{ isExpanded?: boolean }>()((theme: Theme, { isExpanded }) => ({
  depositsSection: {
    minWidth: '550px',
    maxWidth: '609px',
  },
  otherDepositsSection: {
    minWidth: '609px',
    marginTop: '20px',
  },
  chargesSection: {
    minWidth: '550px',
  },
  devider: {
    marginTop: '24px',
    marginBottom: '25px',
  },
  dateGrid: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },

  headings: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#333333',
  },
  typeSection: {
    minWidth: '550px',
    maxWidth: isExpanded ? null : '609px',
    maxHeight: isExpanded ? '216px' : '350px',
    minHeight: isExpanded ? '234px' : null,
  },
}));

export const Housing = () => {
  const isExpanded = useSelector(selectExpandedModsDialog);
  const currentModDetails = useSelector(selectCurrentModDetails);
  const { t } = useTranslation();
  const { classes } = useStyles({ isExpanded });
  const { setValue, register, getValues } = useFormContext();
  register('deposits');
  const deposits = getValues('deposits') ? getValues('deposits') : currentModDetails?.deposits;
  setValue('deposits', deposits);

  return (
    <>
      <div className={classes.typeSection}>
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={4}
          flexGrow={1}
          sx={{ maxHeight: isExpanded ? '200px' : '350px' }}
        >
          <Grid item xs={isExpanded ? 4 : 6} order={1}>
            <HousingProvider />
          </Grid>
          <Grid item xs={isExpanded ? 4 : 6} order={isExpanded ? 4 : 3}>
            <TravelerRequestedHousingType />
          </Grid>
          <Grid item xs={isExpanded ? 4 : 6} order={isExpanded ? 7 : 5}>
            <ActualHousingType />
          </Grid>
          <Grid
            item
            xs={isExpanded ? 4 : 6}
            order={isExpanded ? 2 : 7}
            sx={{ paddingLeft: isExpanded ? '36px !important' : null }}
          >
            <ReasonForChange />
          </Grid>
          <Grid item xs={isExpanded ? 4 : 6} order={isExpanded ? 5 : 2} sx={{ paddingLeft: '36px !important' }}>
            <StandardAmountCharged />
          </Grid>
          <Grid item xs={isExpanded ? 4 : 6} order={isExpanded ? 8 : 4} sx={{ paddingLeft: '36px !important' }}>
            <AmountToTraveler />
          </Grid>
          <Grid item xs={isExpanded ? 4 : 6} order={isExpanded ? 3 : 6} sx={{ paddingLeft: '36px !important' }}>
            <MonthlyLodgingAmount />
          </Grid>
          <Grid
            xs={isExpanded ? 4 : 6}
            item
            alignItems="center"
            alignSelf="center"
            order={isExpanded ? 6 : 8}
            sx={{ paddingLeft: '36px !important' }}
          >
            <BillableToClient />
          </Grid>
        </Grid>
      </div>
      <Grid className={classes.devider}>
        <Divider />
      </Grid>
      <div className={classes.chargesSection}>
        <div style={{ marginTop: '-5px', marginBottom: '15px' }}>
          <Typography component="span" className={classes.headings}>{`${t(
            'placement.placementMods.additionalCharges',
          )}`}</Typography>
        </div>
        <Grid container spacing={2} className={classes.dateGrid}>
          <Grid item xs={4}>
            <PetRentCharges />
          </Grid>
          <Grid item xs={4}>
            <ParkingGarageCharges />
          </Grid>
          <Grid item xs={4}>
            <UtilityCharges />
          </Grid>
        </Grid>
      </div>
      <Grid className={classes.devider}>
        <Divider />
      </Grid>
      <div className={classes.depositsSection}>
        <div style={{ marginTop: '-5px', marginBottom: '15px' }}>
          <Typography component="span" className={classes.headings}>{`${t(
            'placement.placementMods.deposits',
          )}`}</Typography>
        </div>
        <Grid container spacing={2} className={classes.dateGrid}>
          <Grid item xs={6}>
            <PetDepositAmount />
          </Grid>
          <Grid item xs={6}>
            <PetAmountRefundableField />
          </Grid>
          <Grid item xs={6}>
            <Private2BedroomDepositAmount />
          </Grid>
          <Grid item xs={6}>
            <TwoBedroomRefundableField />
          </Grid>
          <Grid item xs={6}>
            <Private3BedroomDepositAmount />
          </Grid>
          <Grid item xs={6}>
            <ThreeBedroomRefundableField />
          </Grid>
        </Grid>
      </div>
      <div className={classes.otherDepositsSection}>
        <Grid container spacing={2} className={classes.dateGrid}>
          <Grid item xs={isExpanded ? 3 : 6} sx={{ maxWidth: '310px !important' }}>
            <OtherDepositDescription />
          </Grid>
          <Grid item xs={3}>
            <OtherDepositAmount />
          </Grid>
          <Grid item xs={3}>
            <RefundableField />
          </Grid>
        </Grid>
      </div>
    </>
  );
};
